import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, fmt } from "@/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Package, Tag, ShoppingBag, Users, Layers, BarChart3, Trash2, Plus, Settings as SettingsIcon, Mail, Pencil, Eye, EyeOff, Upload, X, FolderTree } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const ORDER_STATUSES = ["placed","confirmed","packed","shipped","out_for_delivery","delivered","cancelled","returned","refunded"];

export default function Admin() {
  const { user, authLoading } = useApp();
  if (authLoading) return <div className="p-20 text-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/login?next=/admin" />;
  if (user.role !== "admin") return <div className="p-20 text-center"><h1 className="font-serif text-2xl">Admin access only</h1></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="section-label">Admin Console</div>
      <h1 className="font-serif text-4xl mt-2">DivineCart Dashboard</h1>
      <Tabs defaultValue="dash" className="mt-8">
        <TabsList className="bg-muted">
          <TabsTrigger value="dash" data-testid="admin-tab-dash"><BarChart3 className="w-4 h-4 mr-2"/>Dashboard</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-tab-products"><Package className="w-4 h-4 mr-2"/>Products</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-tab-categories"><FolderTree className="w-4 h-4 mr-2"/>Categories</TabsTrigger>
          <TabsTrigger value="kits" data-testid="admin-tab-kits"><Layers className="w-4 h-4 mr-2"/>Puja Kits</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders"><ShoppingBag className="w-4 h-4 mr-2"/>Orders</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="admin-tab-coupons"><Tag className="w-4 h-4 mr-2"/>Coupons</TabsTrigger>
          <TabsTrigger value="customers" data-testid="admin-tab-customers"><Users className="w-4 h-4 mr-2"/>Customers</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages"><Mail className="w-4 h-4 mr-2"/>Messages</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings"><SettingsIcon className="w-4 h-4 mr-2"/>Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="dash"><Dashboard/></TabsContent>
        <TabsContent value="products"><Products/></TabsContent>
        <TabsContent value="categories"><Categories/></TabsContent>
        <TabsContent value="kits"><KitsAdmin/></TabsContent>
        <TabsContent value="orders"><Orders/></TabsContent>
        <TabsContent value="coupons"><Coupons/></TabsContent>
        <TabsContent value="customers"><Customers/></TabsContent>
        <TabsContent value="messages"><Messages/></TabsContent>
        <TabsContent value="settings"><SiteSettings/></TabsContent>
      </Tabs>
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(()=>{ api.get("/admin/dashboard").then(({data})=>setD(data)); }, []);
  if (!d) return <div className="py-10">Loading...</div>;
  const stat = (t,v,c) => (
    <div className="rounded-2xl p-5 border border-border bg-white">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{t}</div>
      <div className={`font-serif text-3xl mt-2 ${c||"text-primary"}`}>{v}</div>
    </div>
  );
  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stat("Today's Sales", fmt(d.today_sales))}
        {stat("Total Sales", fmt(d.total_sales))}
        {stat("Total Orders", d.total_orders)}
        {stat("Customers", d.customers)}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border bg-white p-5">
          <h3 className="font-serif text-xl mb-4">Orders by Status</h3>
          <div className="space-y-2 text-sm">
            {Object.entries(d.by_status).map(([k,v])=>(<div key={k} className="flex justify-between"><span className="capitalize">{k}</span><b>{v}</b></div>))}
            {Object.keys(d.by_status).length===0 && <div className="text-muted-foreground">No orders yet.</div>}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-white p-5">
          <h3 className="font-serif text-xl mb-4">Low Stock</h3>
          <div className="space-y-2 text-sm">
            {d.low_stock.length===0 && <div className="text-muted-foreground">All products well stocked.</div>}
            {d.low_stock.map(p=>(<div key={p.id} className="flex justify-between"><span>{p.name}</span><span className="text-accent font-bold">{p.stock} left</span></div>))}
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-white p-5">
        <h3 className="font-serif text-xl mb-4">Recent Orders</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2">Order</th><th>Customer</th><th>Status</th><th>Total</th></tr></thead>
            <tbody>{d.recent_orders.map(o=>(<tr key={o.id} className="border-b border-border/60"><td className="py-2">{o.order_no}</td><td>{o.user_name}</td><td className="capitalize">{o.status}</td><td>{fmt(o.total)}</td></tr>))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const EMPTY_PRODUCT = { name:"", slug:"", category:"puja-samagri", description:"", mrp:0, price:0, stock:100, weight:"", image:"", images:[], is_active:true, is_best_seller:false, is_featured:false, is_new:false };

function ProductForm({ f, setF, cats, onSave, saving }) {
  const fileRef = useRef();
  const uploadFile = async (file) => {
    const form = new FormData(); form.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND_URL}${data.url}`;
      const newImages = [...(f.images || []).filter(Boolean), url];
      setF({ ...f, images: newImages, image: f.image || url });
      toast.success("Image uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const removeImage = (idx) => {
    const next = (f.images||[]).filter((_,i)=>i!==idx);
    setF({ ...f, images: next, image: next[0] || "" });
  };
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <input data-testid="pf-name" className="pk-input" placeholder="Name" value={f.name} onChange={e=>setF({...f, name:e.target.value, slug: f.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"")})}/>
        <input data-testid="pf-slug" className="pk-input" placeholder="Slug (URL)" value={f.slug} onChange={e=>setF({...f, slug:e.target.value})}/>
        <select data-testid="pf-category" className="pk-input" value={f.category} onChange={e=>setF({...f, category:e.target.value})}>
          {cats.map(c=><option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <input data-testid="pf-weight" className="pk-input" placeholder="Weight / Pack Size (e.g. 100g)" value={f.weight} onChange={e=>setF({...f, weight:e.target.value})}/>
        <input data-testid="pf-mrp" className="pk-input" type="number" placeholder="MRP (₹)" value={f.mrp} onChange={e=>setF({...f, mrp:+e.target.value})}/>
        <input data-testid="pf-price" className="pk-input" type="number" placeholder="Selling Price (₹)" value={f.price} onChange={e=>setF({...f, price:+e.target.value})}/>
        <input data-testid="pf-stock" className="pk-input" type="number" placeholder="Stock" value={f.stock} onChange={e=>setF({...f, stock:+e.target.value})}/>
        <input data-testid="pf-brand" className="pk-input" placeholder="Brand" value={f.brand || "DivineCart"} onChange={e=>setF({...f, brand:e.target.value})}/>
      </div>
      <textarea data-testid="pf-desc" rows={3} className="pk-input mt-3" placeholder="Description" value={f.description} onChange={e=>setF({...f, description:e.target.value})}/>

      <div className="mt-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Product Images</div>
        <div className="grid grid-cols-4 gap-2 mb-2">
          {(f.images||[]).filter(Boolean).map((img, idx) => (
            <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
              <img src={img} className="w-full h-full object-cover"/>
              {idx === 0 && <span className="absolute top-1 left-1 text-[10px] bg-primary text-white px-1.5 py-0.5 rounded">Main</span>}
              <button type="button" onClick={()=>removeImage(idx)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 grid place-items-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3 text-primary"/></button>
            </div>
          ))}
          <button type="button" data-testid="pf-upload-btn" onClick={()=>fileRef.current?.click()} className="aspect-square rounded-lg border-2 border-dashed border-border grid place-items-center text-muted-foreground hover:border-primary hover:text-primary transition-colors">
            <div className="text-center"><Upload className="w-5 h-5 mx-auto"/><div className="text-[10px] mt-1">Upload</div></div>
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e=>{ if(e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value=""; }}/>
        <div className="text-xs text-muted-foreground">Or paste an image URL:</div>
        <div className="flex gap-2 mt-1">
          <input data-testid="pf-image-url" className="pk-input flex-1" placeholder="https://..." value={f.image} onChange={e=>setF({...f, image:e.target.value})}/>
          <button type="button" onClick={()=>{ if(f.image) setF({...f, images:[...(f.images||[]).filter(Boolean), f.image]}); }} className="px-3 py-2 rounded-lg bg-primary text-white text-sm">Add</button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.is_active} onChange={e=>setF({...f, is_active:e.target.checked})}/>Active (visible on site)</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.is_best_seller} onChange={e=>setF({...f, is_best_seller:e.target.checked})}/>Best Seller</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.is_featured} onChange={e=>setF({...f, is_featured:e.target.checked})}/>Featured</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.is_new} onChange={e=>setF({...f, is_new:e.target.checked})}/>New Arrival</label>
      </div>

      {/* Product Variants */}
      <div className="mt-6 border-t border-border pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Variants (Size / Color / SKU) — leave empty for simple product</div>
          <button type="button" data-testid="pf-add-variant" onClick={()=>setF({...f, variants: [...(f.variants||[]), { id: Math.random().toString(36).slice(2,10), name:"", size:"", color:"", sku:"", price: f.price, mrp: f.mrp, stock: 0, is_active: true }]})} className="text-xs px-2 py-1 rounded bg-muted">+ Add Variant</button>
        </div>
        {(f.variants || []).map((v, i) => (
          <div key={v.id} className="grid grid-cols-[80px_80px_100px_90px_90px_70px_auto] gap-1 mb-1 items-center text-xs">
            <input placeholder="Size" value={v.size} onChange={e=>{const arr=[...f.variants]; arr[i]={...v, size:e.target.value, name:`${e.target.value} / ${v.color}`.replace(/ \/ $|^ \/ /,"")}; setF({...f, variants:arr});}} className="pk-input py-1"/>
            <input placeholder="Color" value={v.color} onChange={e=>{const arr=[...f.variants]; arr[i]={...v, color:e.target.value, name:`${v.size} / ${e.target.value}`.replace(/ \/ $|^ \/ /,"")}; setF({...f, variants:arr});}} className="pk-input py-1"/>
            <input placeholder="SKU" value={v.sku} onChange={e=>{const arr=[...f.variants]; arr[i]={...v, sku:e.target.value}; setF({...f, variants:arr});}} className="pk-input py-1"/>
            <input type="number" placeholder="Price" value={v.price} onChange={e=>{const arr=[...f.variants]; arr[i]={...v, price:+e.target.value}; setF({...f, variants:arr});}} className="pk-input py-1"/>
            <input type="number" placeholder="MRP" value={v.mrp} onChange={e=>{const arr=[...f.variants]; arr[i]={...v, mrp:+e.target.value}; setF({...f, variants:arr});}} className="pk-input py-1"/>
            <input type="number" placeholder="Stock" value={v.stock} onChange={e=>{const arr=[...f.variants]; arr[i]={...v, stock:+e.target.value}; setF({...f, variants:arr});}} className="pk-input py-1"/>
            <button type="button" onClick={()=>setF({...f, variants: f.variants.filter((_,j)=>j!==i)})} className="text-primary text-sm">✕</button>
          </div>
        ))}
      </div>

      <button data-testid="pf-save-btn" disabled={saving} onClick={onSave} className="pk-btn-primary mt-4 w-full">
        {saving ? "Saving..." : "Save Product"}
      </button>
    </>
  );
}

function Products() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [f, setF] = useState(EMPTY_PRODUCT);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const load = ()=> api.get("/admin/products").then(({data})=>setItems(data));
  useEffect(()=>{ load(); api.get("/categories").then(({data})=>setCats(data)); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/products", f);
      toast.success("Product added"); load(); setF(EMPTY_PRODUCT); setAddOpen(false);
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const update = async () => {
    setSaving(true);
    try {
      await api.put(`/products/${editing.id}`, f);
      toast.success("Product updated"); load(); setEditOpen(false); setEditing(null);
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const del = async (id) => { if(!window.confirm("Delete permanently?")) return; await api.delete(`/products/${id}`); load(); toast.success("Deleted"); };
  const toggleVis = async (id) => { const { data } = await api.patch(`/products/${id}/visibility`); load(); toast.success(data.is_active ? "Now visible on site" : "Hidden from site"); };
  const updateStock = async (id, stock) => { await api.put(`/products/${id}`, { stock: Number(stock) }); load(); };
  const openEdit = (p) => { setEditing(p); setF({...EMPTY_PRODUCT, ...p, images: p.images && p.images.length ? p.images : (p.image ? [p.image] : [])}); setEditOpen(true); };

  const filtered = items.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.category.includes(search.toLowerCase()));

  return (
    <div className="mt-6 space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <input data-testid="admin-product-search" className="pk-input flex-1" placeholder="Search products by name or category..." value={search} onChange={e=>setSearch(e.target.value)}/>
        <Dialog open={addOpen} onOpenChange={(o)=>{ setAddOpen(o); if(!o) setF(EMPTY_PRODUCT); }}>
          <DialogTrigger asChild><button data-testid="admin-add-product-btn" className="pk-btn-primary"><Plus className="w-4 h-4"/>Add Product</button></DialogTrigger>
          <DialogContent className="bg-background max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add New Product</DialogTitle></DialogHeader>
            <ProductForm f={f} setF={setF} cats={cats} onSave={create} saving={saving}/>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-background max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Product</DialogTitle></DialogHeader>
          <ProductForm f={f} setF={setF} cats={cats} onSave={update} saving={saving}/>
        </DialogContent>
      </Dialog>

      <div className="rounded-2xl border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left">
            <tr><th className="p-3">Image</th><th>Name</th><th>Category</th><th>Price</th><th>Stock</th><th>Status</th><th className="text-right pr-4">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.map(p=>(
              <tr key={p.id} className={`border-t border-border ${p.is_active===false?"opacity-60":""}`}>
                <td className="p-2"><img src={p.image} alt="" className="w-12 h-12 rounded object-cover border border-border"/></td>
                <td>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">/{p.slug}</div>
                </td>
                <td className="capitalize">{p.category?.replace(/-/g," ")}</td>
                <td>
                  <div className="font-semibold text-primary">{fmt(p.price)}</div>
                  {p.mrp>p.price && <div className="text-xs line-through text-muted-foreground">{fmt(p.mrp)}</div>}
                </td>
                <td>
                  <input type="number" data-testid={`admin-stock-${p.slug}`} defaultValue={p.stock} onBlur={e=>updateStock(p.id, e.target.value)} className={`w-20 pk-input py-1 ${p.stock<10?"text-accent font-bold":""}`}/>
                </td>
                <td>
                  {p.is_active===false ? <span className="text-xs px-2 py-1 rounded-full bg-muted">Hidden</span> :
                   p.stock<=0 ? <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800">Out of stock</span> :
                   <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">Live</span>}
                </td>
                <td className="text-right pr-4">
                  <div className="inline-flex gap-1">
                    <button data-testid={`admin-toggle-vis-${p.slug}`} title={p.is_active===false?"Show":"Hide"} onClick={()=>toggleVis(p.id)} className="p-2 hover:bg-muted rounded">
                      {p.is_active===false ? <EyeOff className="w-4 h-4 text-muted-foreground"/> : <Eye className="w-4 h-4"/>}
                    </button>
                    <button data-testid={`admin-edit-${p.slug}`} title="Edit" onClick={()=>openEdit(p)} className="p-2 hover:bg-muted rounded"><Pencil className="w-4 h-4"/></button>
                    <button data-testid={`admin-del-product-${p.slug}`} title="Delete" onClick={()=>del(p.id)} className="p-2 hover:bg-muted rounded"><Trash2 className="w-4 h-4 text-primary"/></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length===0 && <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">No products match your search.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Categories() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name:"", slug:"", image:"", description:"" });
  const [editingId, setEditingId] = useState(null);
  const load = ()=> api.get("/categories").then(({data})=>setItems(data));
  useEffect(()=>{ load(); }, []);
  const save = async () => {
    try {
      if (editingId) { await api.put(`/categories/${editingId}`, f); toast.success("Category updated"); }
      else { await api.post("/categories", f); toast.success("Category created"); }
      load(); setF({ name:"", slug:"", image:"", description:"" }); setEditingId(null);
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if(!window.confirm("Delete category? Products remain but lose the category badge.")) return; await api.delete(`/categories/${id}`); load(); };
  const edit = (c) => { setEditingId(c.id); setF({ name:c.name, slug:c.slug, image:c.image||"", description:c.description||"" }); };
  return (
    <div className="mt-6 grid md:grid-cols-[1fr_360px] gap-6">
      <div className="rounded-2xl border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left"><tr><th className="p-3">Image</th><th>Name</th><th>Slug</th><th className="text-right pr-4">Actions</th></tr></thead>
          <tbody>
            {items.map(c=>(
              <tr key={c.id} className="border-t border-border">
                <td className="p-2"><img src={c.image} alt="" className="w-10 h-10 rounded object-cover border border-border"/></td>
                <td className="font-medium">{c.name}</td>
                <td className="text-xs text-muted-foreground">/{c.slug}</td>
                <td className="text-right pr-4">
                  <button data-testid={`admin-cat-edit-${c.slug}`} onClick={()=>edit(c)} className="p-2"><Pencil className="w-4 h-4"/></button>
                  <button data-testid={`admin-cat-del-${c.slug}`} onClick={()=>del(c.id)} className="p-2"><Trash2 className="w-4 h-4 text-primary"/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rounded-2xl border border-border bg-white p-5">
        <h3 className="font-serif text-xl">{editingId ? "Rename Category" : "Add Category"}</h3>
        <div className="mt-3 space-y-2">
          <input data-testid="admin-cat-name" className="pk-input" placeholder="Name (e.g. Puja Thali)" value={f.name} onChange={e=>setF({...f, name:e.target.value, slug: editingId? f.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g,"-")})}/>
          <input data-testid="admin-cat-slug" className="pk-input" placeholder="Slug (URL)" value={f.slug} onChange={e=>setF({...f, slug:e.target.value})}/>
          <input data-testid="admin-cat-image" className="pk-input" placeholder="Image URL (optional)" value={f.image} onChange={e=>setF({...f, image:e.target.value})}/>
          <textarea rows={2} className="pk-input" placeholder="Description (optional)" value={f.description} onChange={e=>setF({...f, description:e.target.value})}/>
          <button data-testid="admin-cat-save-btn" onClick={save} className="pk-btn-primary w-full">{editingId ? "Update" : "Add Category"}</button>
          {editingId && <button onClick={()=>{ setEditingId(null); setF({name:"",slug:"",image:"",description:""}); }} className="w-full text-sm text-muted-foreground">Cancel</button>}
        </div>
      </div>
    </div>
  );
}

function KitsAdmin() {
  const [kits, setKits] = useState([]);
  const [kow, setKow] = useState({ enabled:false, kit_slug:"", title:"", description:"", image:"", banner:"" });
  const load = async ()=> {
    const [{data: k}, {data: current}] = await Promise.all([
      api.get("/kits"), api.get("/kit-of-week").catch(()=>({data:null}))
    ]);
    setKits(k);
    if (current) setKow({ enabled:true, kit_slug:current.slug, title:current.featured_title||"", description:current.featured_description||"", image:current.featured_image||"", banner:current.featured_banner||"" });
  };
  useEffect(()=>{ load(); }, []);
  const del = async (id) => { if(!window.confirm("Delete kit?")) return; await api.delete(`/kits/${id}`); load(); };
  const saveKow = async () => {
    if (!kow.kit_slug) { toast.error("Choose a kit"); return; }
    try { await api.put("/admin/kit-of-week", kow); toast.success("Kit of the Week updated"); }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="mt-6 space-y-6">
      {/* Kit of the Week card */}
      <div className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-secondary/20 to-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs uppercase tracking-widest text-primary font-medium">✨ Kit of the Week — Homepage Feature</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <select data-testid="kow-select" className="pk-input" value={kow.kit_slug} onChange={e=>setKow({...kow, kit_slug:e.target.value})}>
            <option value="">— Select a Puja Kit —</option>
            {kits.map(k=><option key={k.id} value={k.slug}>{k.name}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!kow.enabled} onChange={e=>setKow({...kow, enabled:e.target.checked})}/>Enable on homepage</label>
          <input data-testid="kow-title" className="pk-input md:col-span-2" placeholder="Featured Title (optional — overrides kit name)" value={kow.title} onChange={e=>setKow({...kow, title:e.target.value})}/>
          <textarea rows={2} data-testid="kow-desc" className="pk-input md:col-span-2" placeholder="Featured Description (optional)" value={kow.description} onChange={e=>setKow({...kow, description:e.target.value})}/>
          <input data-testid="kow-image" className="pk-input" placeholder="Featured Image URL (optional)" value={kow.image} onChange={e=>setKow({...kow, image:e.target.value})}/>
          <input data-testid="kow-banner" className="pk-input" placeholder="Festive Banner Image URL (optional)" value={kow.banner} onChange={e=>setKow({...kow, banner:e.target.value})}/>
        </div>
        <button data-testid="kow-save-btn" onClick={saveKow} className="pk-btn-primary mt-3">Save Kit of the Week</button>
      </div>

      <div className="text-sm text-muted-foreground">All Puja Kits</div>
      <div className="grid md:grid-cols-2 gap-4">
        {kits.map(k=>(
          <div key={k.id} className="p-4 rounded-xl border border-border bg-white">
            <div className="flex justify-between items-start gap-3">
              <div>
                <div className="text-xs uppercase text-muted-foreground">{k.puja} · {k.kit_type}</div>
                <div className="font-serif text-lg">{k.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{k.items.length} items · {fmt(k.price)}</div>
              </div>
              <button onClick={()=>del(k.id)} className="p-2 text-primary"><Trash2 className="w-4 h-4"/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({ status:"", payment_method:"", payment_status:"", date_from:"", date_to:"" });
  const load = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([_,v])=>v));
    const { data } = await api.get("/admin/orders", { params });
    setOrders(data);
  };
  useEffect(()=>{ load(); /* eslint-disable-next-line */ }, []);
  const upd = async (id, status) => { await api.put(`/admin/orders/${id}/status`, { status }); toast.success("Updated"); load(); };

  const downloadBlob = (blob, name) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };
  const dlSingle = async (o) => {
    try {
      const res = await api.get(`/admin/orders/${o.id}/label`, { responseType: "blob" });
      downloadBlob(res.data, `label-${o.order_no}.pdf`);
    } catch { toast.error("Failed to generate label"); }
  };
  const dlBulk = async (mode) => {
    try {
      const body = mode === "filtered"
        ? { filters: Object.fromEntries(Object.entries(filters).filter(([_,v])=>v)) }
        : { order_ids: orders.map(o=>o.id) };
      const res = await api.post("/admin/orders/labels", body, { responseType: "blob" });
      downloadBlob(res.data, `labels-${orders.length}-orders.pdf`);
      toast.success(`${orders.length} label(s) downloaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div className="mt-6 space-y-4">
      <div className="rounded-2xl border border-border bg-white p-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Filters</div>
        <div className="grid md:grid-cols-6 gap-2">
          <select data-testid="orders-filter-status" className="pk-input" value={filters.status} onChange={e=>setFilters({...filters, status:e.target.value})}>
            <option value="">All Statuses</option>
            {ORDER_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="orders-filter-pm" className="pk-input" value={filters.payment_method} onChange={e=>setFilters({...filters, payment_method:e.target.value})}>
            <option value="">All Payment Methods</option>
            <option value="cod">COD</option>
            <option value="razorpay">Razorpay</option>
            <option value="phonepe">PhonePe</option>
          </select>
          <select data-testid="orders-filter-pstatus" className="pk-input" value={filters.payment_status} onChange={e=>setFilters({...filters, payment_status:e.target.value})}>
            <option value="">All Payment Status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="cod">COD Unpaid</option>
          </select>
          <input type="date" className="pk-input" value={filters.date_from} onChange={e=>setFilters({...filters, date_from:e.target.value})}/>
          <input type="date" className="pk-input" value={filters.date_to} onChange={e=>setFilters({...filters, date_to:e.target.value})}/>
          <button data-testid="orders-apply-filters" onClick={load} className="pk-btn-primary">Apply</button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button data-testid="orders-download-all-labels" onClick={()=>dlBulk("all")} disabled={!orders.length} className="pk-btn-secondary disabled:opacity-50">
            📥 Download {orders.length} Labels (4×6)
          </button>
          {hasFilters && (
            <button data-testid="orders-download-filtered-labels" onClick={()=>dlBulk("filtered")} className="pk-btn-primary">
              📥 Download Filtered Labels
            </button>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left"><tr><th className="p-3">Order</th><th>Customer</th><th>Items</th><th>Total</th><th>Payment</th><th>Status</th><th>Label</th></tr></thead>
          <tbody>
            {orders.map(o=>(
              <tr key={o.id} className="border-t border-border">
                <td className="p-3 font-medium">{o.order_no}<div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</div></td>
                <td>{o.user_name}<div className="text-xs text-muted-foreground">{o.address?.phone || o.user_email}</div></td>
                <td>{o.items.length}</td>
                <td>{fmt(o.total)}</td>
                <td className="uppercase text-xs">{o.payment_method}<div className="text-[10px] text-muted-foreground">{o.payment_status}</div></td>
                <td>
                  <select data-testid={`admin-order-status-${o.order_no}`} value={o.status} onChange={e=>upd(o.id, e.target.value)} className="pk-input py-1">
                    {ORDER_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td>
                  <button data-testid={`admin-order-label-${o.order_no}`} onClick={()=>dlSingle(o)} className="px-3 py-1 rounded-md bg-primary text-white text-xs">Label</button>
                </td>
              </tr>
            ))}
            {orders.length===0 && <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">No orders match the current filters</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Coupons() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ code:"", type:"percent", value:10, min_order:499, usage_limit:1000, active:true });
  const load = ()=> api.get("/coupons").then(({data})=>setItems(data));
  useEffect(()=>{load()}, []);
  const save = async () => { try{ await api.post("/coupons", f); toast.success("Coupon created"); load(); }catch{toast.error("Failed")}};
  const del = async (id) => { await api.delete(`/coupons/${id}`); load(); };
  return (
    <div className="mt-6 grid md:grid-cols-[1fr_360px] gap-6">
      <div className="rounded-2xl border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left"><tr><th className="p-3">Code</th><th>Type</th><th>Value</th><th>Min Order</th><th>Used</th><th></th></tr></thead>
          <tbody>
            {items.map(c=>(<tr key={c.id} className="border-t border-border"><td className="p-3 font-bold text-primary">{c.code}</td><td>{c.type}</td><td>{c.type==="percent"?`${c.value}%`:fmt(c.value)}</td><td>{fmt(c.min_order)}</td><td>{c.used}/{c.usage_limit}</td><td><button onClick={()=>del(c.id)}><Trash2 className="w-4 h-4 text-primary"/></button></td></tr>))}
          </tbody>
        </table>
      </div>
      <div className="rounded-2xl border border-border bg-white p-5">
        <h3 className="font-serif text-xl">Add Coupon</h3>
        <div className="mt-3 space-y-2">
          <input className="pk-input" placeholder="CODE" value={f.code} onChange={e=>setF({...f, code:e.target.value.toUpperCase()})}/>
          <select className="pk-input" value={f.type} onChange={e=>setF({...f, type:e.target.value})}><option value="percent">Percent</option><option value="flat">Flat</option></select>
          <input className="pk-input" type="number" placeholder="Value" value={f.value} onChange={e=>setF({...f, value:+e.target.value})}/>
          <input className="pk-input" type="number" placeholder="Min Order" value={f.min_order} onChange={e=>setF({...f, min_order:+e.target.value})}/>
          <button onClick={save} className="pk-btn-primary w-full">Save</button>
        </div>
      </div>
    </div>
  );
}

function Customers() {
  const [items, setItems] = useState([]);
  useEffect(()=>{ api.get("/admin/customers").then(({data})=>setItems(data)); }, []);
  return (
    <div className="mt-6 rounded-2xl border border-border bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left"><tr><th className="p-3">Name</th><th>Email</th><th>Phone</th><th>Since</th></tr></thead>
        <tbody>{items.map(u=>(<tr key={u.id} className="border-t border-border"><td className="p-3">{u.name}</td><td>{u.email}</td><td>{u.phone}</td><td>{new Date(u.created_at).toLocaleDateString()}</td></tr>))}</tbody>
      </table>
    </div>
  );
}

function Messages() {
  const [msgs, setMsgs] = useState([]);
  useEffect(()=>{ api.get("/admin/messages").then(({data})=>setMsgs(data)); }, []);
  return (
    <div className="mt-6 space-y-3">
      {msgs.length === 0 && <div className="text-muted-foreground p-6 rounded-xl border border-border bg-white">No contact messages yet.</div>}
      {msgs.map(m=>(
        <div key={m.id} className="rounded-xl border border-border bg-white p-4">
          <div className="flex justify-between text-sm">
            <div><b>{m.name}</b> · <a href={`mailto:${m.email}`} className="text-primary">{m.email}</a>{m.phone && <> · {m.phone}</>}</div>
            <div className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString()}</div>
          </div>
          {m.subject && <div className="mt-1 text-sm font-medium">{m.subject}</div>}
          <div className="mt-2 text-sm whitespace-pre-wrap">{m.message}</div>
        </div>
      ))}
    </div>
  );
}

function SiteSettings() {
  const [s, setS] = useState(null);
  useEffect(()=>{ api.get("/settings").then(({data})=>setS(data)); }, []);
  if (!s) return <div className="p-6">Loading...</div>;
  const set = (k,v) => setS({...s, [k]:v});
  const save = async () => {
    try {
      await api.put("/settings", s);
      const { data } = await api.get("/settings");
      setS(data);
      toast.success("Settings saved successfully.");
    }
    catch { toast.error("Failed to save"); }
  };
  const field = (key, label, placeholder="") => (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>
      <input data-testid={`settings-${key}`} className="pk-input mt-1" placeholder={placeholder} value={s[key]||""} onChange={e=>set(key, e.target.value)}/>
    </div>
  );
  return (
    <div className="mt-6 max-w-3xl">
      <div className="rounded-2xl border border-border bg-white p-6 space-y-4">
        <h3 className="font-serif text-xl">Business Information</h3>
        <p className="text-sm text-muted-foreground">These values are shown on the Contact page, Footer, About page, policy pages and used in JSON-LD SEO. Please keep them accurate for payment gateway verification.</p>
        {field("brand","Brand Name")}
        {field("legal_name","Legal Entity Name","e.g. DivineCart Retail (Proprietor: Your Name)")}
        {field("email","Support Email","support@yourdomain.in")}
        {field("phone","Support Phone (with country code)","+91XXXXXXXXXX")}
        {field("whatsapp","WhatsApp Number","+91XXXXXXXXXX")}
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Registered Business Address</label>
          <textarea data-testid="settings-address" rows={3} className="pk-input mt-1" value={s.address||""} onChange={e=>set("address", e.target.value)}/>
        </div>
        {field("gstin","GSTIN (optional)","22AAAAA0000A1Z5")}
        {field("support_hours","Support Hours","Mon–Sat, 10:00 AM – 7:00 PM IST")}
        <button data-testid="settings-save-btn" onClick={save} className="pk-btn-primary">Save Settings</button>
      </div>
    </div>
  );
}
